import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Alert, AlertTitle, Box, DialogActions, DialogContent, DialogTitle, Unstable_Grid2 as Grid} from "@mui/material";
import Dialog from "../../base/dialog";
import Form, {MuiRFF, Yup} from "../../form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import WarehouseAutocomplete from "../../text-fields/autocomplete/warehouse-autocomplete";
import WarehouseV2Autocomplete from "../../text-fields/autocomplete/warehouse-v2-autocomplete";
import Button from "../../base/button/button";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {useTranslation} from "react-i18next";
import DateUtils from "../../../../core/services/date-utils";
import EnvService from "../../../../core/services/env";
import {InventoryTransferStatuses} from "../../../../core/constants/enums";
import {routeFunctions} from "../../../../core/models/static/routes/app-routes";
import {useHistory} from "react-router-dom";
import ToastService from "../../../../core/services/toast";

const formNames = {
    fromWarehouse: 'fromWarehouse',
    toWarehouse: 'toWarehouse',
    requiredDate: 'requiredDate',
};

const openTransferStatuses = [InventoryTransferStatuses.pending,];
const existingChannelPageSize = 50;
const existingChannelMaxPages = 10;

/**
 * Maps a warehouse record to the autocomplete option shape (v1 or v2).
 */
const mapWarehouseToOption = (warehouse) => {
    if (!warehouse) {
        return null;
    }
    const title = warehouse.title ?? warehouse.name;
    return {
        id: warehouse.id,
        code: warehouse.code,
        name: title ?? warehouse.name,
        title: title,
    };
};

const CreateInventoryTransferDialog = ({
                                           open,
                                           close,
                                           onCreated,
                                           initialFromWarehouse = null,
                                           initialToWarehouse = null,
                                       }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'inventory-transfer.create-transfer'});
    const [submitting, setSubmitting] = useState(false);
    const [existingTransfer, setExistingTransfer] = useState(null);
    const isMounted = useIsMounted();
    const history = useHistory();
    const formId = useMemo(() => `create-inventory-transfer-form`, []);
    const useWarehouseV2 = EnvService.DEV_useWarehouseV2InTransfers;
    const WarehousePicker = useWarehouseV2 ? WarehouseV2Autocomplete : WarehouseAutocomplete;

    const schema = useMemo(() => Yup.object().shape({
        [formNames.fromWarehouse]: Yup.mixed().nullable().required(ValidateMessages.required),
        [formNames.toWarehouse]: Yup.mixed()
            .nullable()
            .required(ValidateMessages.required)
            .test('different-warehouses', t('differentWarehouses'), function (value) {
                const fromWarehouse = this.parent?.[formNames.fromWarehouse];
                if (!value || !fromWarehouse) {
                    return true;
                }
                if (value.id != null && fromWarehouse.id != null) {
                    return value.id !== fromWarehouse.id;
                }
                if (value.code != null && fromWarehouse.code != null) {
                    return value.code !== fromWarehouse.code;
                }
                return true;
            }),
        [formNames.requiredDate]: Yup.mixed().validDate().nullable().required(ValidateMessages.required),
    }), [t]);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.fromWarehouse]: mapWarehouseToOption(initialFromWarehouse),
        [formNames.toWarehouse]: mapWarehouseToOption(initialToWarehouse),
        [formNames.requiredDate]: null,
    }), [initialFromWarehouse, initialToWarehouse]);

    const formKey = useMemo(
        () => `${open}-${initialFromWarehouse?.id ?? initialFromWarehouse?.code ?? ''}-${initialToWarehouse?.id ?? initialToWarehouse?.code ?? ''}`,
        [initialFromWarehouse?.code, initialFromWarehouse?.id, initialToWarehouse?.code, initialToWarehouse?.id, open],
    );

    /**
     * Clears the existing-channel alert when the dialog is closed.
     */
    useEffect(() => {
        if (!open) {
            setExistingTransfer(null);
        }
    }, [open]);

    /**
     * Finds an open transfer for the selected from/to warehouse route.
     */
    const findOpenTransferForRoute = useCallback(async (fromWarehouse, toWarehouse) => {
        const fromCode = fromWarehouse?.code;
        const toCode = toWarehouse?.code;
        const fromId = fromWarehouse?.id;
        const toId = toWarehouse?.id;
        if (!fromCode && fromId == null) {
            return null;
        }
        if (!toCode && toId == null) {
            return null;
        }

        for (let currentPage = 1; currentPage <= existingChannelMaxPages; currentPage++) {
            const response = await Api.listInventoryTransfers({
                statusNames: openTransferStatuses,
                pagination: {
                    currentPage,
                    pageSize: existingChannelPageSize,
                },
                sorts: [],
            }, undefined, false);
            if (!isMounted()) {
                return null;
            }
            if (!response?.resultFlag) {
                return null;
            }
            const items = response.data?.items ?? [];
            const match = items.find((item) => {
                const matchesFrom = (fromCode && item.fromWarehouseCode === fromCode)
                    || (fromId != null && item.fromWarehouseId === fromId);
                const matchesTo = (toCode && item.toWarehouseCode === toCode)
                    || (toId != null && item.toWarehouseId === toId);
                return matchesFrom && matchesTo;
            });
            if (match) {
                return match;
            }
            if (!response.data?.hasNextPage) {
                return null;
            }
        }
        return null;
    }, [isMounted]);

    /**
     * Navigates to the existing open transfer for this route.
     */
    const openExistingTransfer = useCallback(() => {
        if (!existingTransfer?.id) {
            return;
        }
        close();
        history.push(routeFunctions.inventory.inventoryTransfer.single(existingTransfer.id));
    }, [close, existingTransfer?.id, history]);

    /**
     * Submits the create transfer form.
     */
    const onSubmit = useCallback(async (values, form) => {
        setSubmitting(true);
        setExistingTransfer(null);
        const fromWarehouse = values[formNames.fromWarehouse];
        const toWarehouse = values[formNames.toWarehouse];
        const response = await Api.createInventoryTransfer({
            fromWarehouseId: fromWarehouse?.id,
            toWarehouseId: toWarehouse?.id,
            requiredDate: DateUtils.UTCToServer(values[formNames.requiredDate]).format(DateUtils.withoutOffsetFormat),
        }, false);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setSubmitting(false);
            form.reset();
            close();
            onCreated?.(response.data);
            return;
        }
        const openTransfer = await findOpenTransferForRoute(fromWarehouse, toWarehouse);
        if (!isMounted()) {
            return;
        }
        setSubmitting(false);
        if (openTransfer) {
            setExistingTransfer(openTransfer);
            return;
        }
        ToastService.error({
            message: response?.message ?? '',
            hideTicketSubmission: true,
        });
    }, [close, findOpenTransferForRoute, isMounted, onCreated]);

    return (
        <Dialog
            open={open}
            setOpen={close}
            maxWidth={'sm'}
            scroll={'paper'}
        >
            <DialogTitle>
                <p className={'text-lg text-color-1 font-weight-bold text-center'}>
                    {t('title')}
                </p>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{my: 2, width: '100%'}}>
                    {
                        !!existingTransfer &&
                        <Alert
                            severity={'warning'}
                            sx={{mb: 2}}
                            action={
                                <Button
                                    variant={'text'}
                                    color={'primary'}
                                    size={'small'}
                                    onClick={openExistingTransfer}
                                >
                                    {t('openExisting')}
                                </Button>
                            }
                        >
                            <AlertTitle>{t('existingChannelTitle')}</AlertTitle>
                            {t('existingChannelMessage', {
                                from: existingTransfer.fromWarehouseCode ?? existingTransfer.fromWarehouseId,
                                to: existingTransfer.toWarehouseCode ?? existingTransfer.toWarehouseId,
                            })}
                        </Alert>
                    }
                    <Form
                        key={formKey}
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        formProps={{
                            id: formId,
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid xs={12} md={6}>
                                <WarehousePicker
                                    form
                                    name={formNames.fromWarehouse}
                                    label={t('fromWarehouse')}
                                    required={required[formNames.fromWarehouse]}
                                    disabled={!!initialFromWarehouse}
                                />
                            </Grid>
                            <Grid xs={12} md={6}>
                                <WarehousePicker
                                    form
                                    name={formNames.toWarehouse}
                                    label={t('toWarehouse')}
                                    required={required[formNames.toWarehouse]}
                                    disabled={!!initialToWarehouse}
                                />
                            </Grid>
                            <Grid xs={12}>
                                <MuiRFF.DatePicker
                                    name={formNames.requiredDate}
                                    label={t('requiredDate')}
                                    required={required[formNames.requiredDate]}
                                />
                            </Grid>
                        </Grid>
                    </Form>
                </Box>
            </DialogContent>
            <DialogActions className={'p-0 m-0'} sx={{p: 0, m: 0, display: 'flex', justifyContent: 'space-between'}}>
                <Button
                    variant={'dialog'}
                    size={'medium'}
                    type={'button'}
                    color={'primary'}
                    borderRadius={'0 8px 0 8px'}
                    onClick={close}
                    disabled={submitting}
                >
                    {t('cancel')}
                </Button>
                <Button
                    variant={'dialog'}
                    size={'medium'}
                    type={'submit'}
                    color={'primary'}
                    borderRadius={'0 8px 0 8px'}
                    form={formId}
                    disabled={submitting}
                >
                    {submitting ? t('creating') : t('create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateInventoryTransferDialog;
